import { useState } from "react";
import { AlertTriangle, BellOff } from "lucide-react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  NotificationListItem,
  NotificationListItemSkeleton,
} from "@/features/engagement/components/NotificationListItem";
import { useMarkAllNotificationsRead, useMarkNotificationRead } from "@/features/engagement/hooks/useMarkNotificationRead";
import { useNotifications } from "@/features/engagement/hooks/useNotifications";
import type { AppNotification } from "@/features/engagement/types/notification";
import { ApiError } from "@/types/api";
import { cn } from "@/utils/cn";

const TABS: Array<{ value: "all" | "unread"; label: string }> = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
];

/**
 * docs/ui/routes.md `/notifications`. Empty state isn't wireframed
 * anywhere in the export (ui-architecture.md §23 explicitly lists it under
 * "Missing but needed") — designed fresh here, matching the Wishlist
 * empty state's own shape (icon + heading + description) since that's the
 * one sibling empty state in this same `features/engagement` folder that
 * *is* wireframed.
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"all" | "unread">("all");
  const [page, setPage] = useState(1);
  const notificationsQuery = useNotifications(tab === "unread" ? { filter: "unread" } : {}, page);
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  function setTabAndResetPage(value: "all" | "unread") {
    setTab(value);
    setPage(1);
  }

  function handleClick(notification: AppNotification) {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.linkTo) {
      navigate(notification.linkTo);
    }
  }

  const totalPages = notificationsQuery.data?.meta.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-[720px] px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-h1 text-foreground">Notifications</h1>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => markAllReadMutation.mutate()}
          isLoading={markAllReadMutation.isPending}
        >
          Mark all as read
        </Button>
      </div>

      <div className="mb-4 flex gap-1 border-b border-border">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTabAndResetPage(item.value)}
            className={cn(
              "border-b-2 px-3 py-2 text-body-sm",
              tab === item.value
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {notificationsQuery.isPending &&
          Array.from({ length: 5 }, (_, index) => <NotificationListItemSkeleton key={index} />)}

        {notificationsQuery.isError && (
          <div className="p-6">
            <EmptyState
              icon={AlertTriangle}
              heading="Couldn't load notifications"
              description={
                notificationsQuery.error instanceof ApiError
                  ? notificationsQuery.error.message
                  : "Something went wrong. Please try again."
              }
              action={{ label: "Retry", onClick: () => void notificationsQuery.refetch() }}
            />
          </div>
        )}

        {notificationsQuery.data?.data.length === 0 && (
          <div className="p-6">
            <EmptyState
              icon={BellOff}
              heading={tab === "unread" ? "You're all caught up" : "No notifications yet"}
              description={
                tab === "unread"
                  ? "You have no unread notifications."
                  : "New messages, price drops on items you've saved, and account updates will show up here."
              }
            />
          </div>
        )}

        {notificationsQuery.data?.data.map((notification) => (
          <NotificationListItem
            key={notification.id}
            notification={notification}
            onClick={() => handleClick(notification)}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-6">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href={page > 1 ? "#" : undefined}
                aria-disabled={page <= 1}
                className={page <= 1 ? "pointer-events-none opacity-50" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  if (page > 1) setPage(page - 1);
                }}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 text-body-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href={page < totalPages ? "#" : undefined}
                aria-disabled={page >= totalPages}
                className={page >= totalPages ? "pointer-events-none opacity-50" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  if (page < totalPages) setPage(page + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
